import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronDown, ChevronRight, Check, Trash2, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import type {
  Activity, Transition, Participant, SelectedComponent,
  ToolDefinition, ToolParameter,
} from './types'
import { isActivity, isTransition } from './geometry'

/** Reconcile an activity's stored parameters with its tool definition so the
 *  panel always shows the tool's full parameter list with DEFAULT values filled
 *  in for any blank — selecting an activity should never show empty boxes when
 *  the tool defines defaults. A user-entered (non-blank) value is preserved. */
function mergeToolParams(
  stored: ToolParameter[],
  def: ToolDefinition | undefined,
): ToolParameter[] {
  const fps = def?.FormalParameters?.FormalParameter
  if (!fps || fps.length === 0) return stored
  const byId = new Map(stored.map(p => [p.id, p]))
  return fps.map(fp => {
    const existing = byId.get(fp.Id)
    const hasValue = !!(existing && existing.value != null && String(existing.value).trim() !== '')
    return {
      id: fp.Id,
      value: hasValue ? existing!.value : (fp.DefaultValue || ''),
      mode: existing?.mode || fp.Mode || 'IN',
      dataType: existing?.dataType || fp.DataType?.BasicType?.Type || 'STRING',
    }
  })
}

interface Props {
  selected: SelectedComponent
  workflow: { activities: Activity[]; transitions: Transition[] }
  participants: Participant[]
  getDistinctToolTypes: () => string[]
  getToolsByType: (type: string) => ToolDefinition[]
  getToolDefinition: (id: string) => ToolDefinition | undefined
  onUpdateActivity: (id: string, updates: Partial<Activity>) => void
  onUpdateTransition: (id: string, updates: Partial<Transition>) => void
  onDelete: (component: SelectedComponent) => void
  onSaveState: () => void
  /** Open the Manage Participants dialog — surfaced as a "Manage roles" link
   *  next to the performer (role) selector. */
  onManageParticipants?: () => void
}

export default function PropertiesPanel({
  selected,
  workflow,
  participants,
  getDistinctToolTypes,
  getToolsByType,
  getToolDefinition,
  onUpdateActivity,
  onUpdateTransition,
  onDelete,
  onSaveState,
  onManageParticipants,
}: Props) {
  if (!selected) {
    return (
      <div style={{
        padding: 24,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', gap: 8,
      }}>
        <span style={{ fontSize: '0.9125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
          Properties
        </span>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6, textAlign: 'center', lineHeight: 1.5 }}>
          Select an activity or transition to edit its properties. Double-click to open.
        </span>
      </div>
    )
  }

  if (isTransition(selected)) {
    return (
      <TransitionProps
        transition={selected}
        workflow={workflow}
        onUpdate={onUpdateTransition}
        onDelete={() => onDelete(selected)}
        onSaveState={onSaveState}
      />
    )
  }

  if (isActivity(selected)) {
    return (
      <ActivityProps
        activity={selected}
        participants={participants}
        getDistinctToolTypes={getDistinctToolTypes}
        getToolsByType={getToolsByType}
        getToolDefinition={getToolDefinition}
        onUpdate={onUpdateActivity}
        onDelete={() => onDelete(selected)}
        onSaveState={onSaveState}
        onManageParticipants={onManageParticipants}
      />
    )
  }

  return null
}

// ---- Activity Properties ----

function ActivityProps({
  activity,
  participants,
  getDistinctToolTypes,
  getToolsByType,
  getToolDefinition,
  onUpdate,
  onDelete,
  onSaveState,
  onManageParticipants,
}: {
  activity: Activity
  participants: Participant[]
  getDistinctToolTypes: () => string[]
  getToolsByType: (type: string) => ToolDefinition[]
  getToolDefinition: (id: string) => ToolDefinition | undefined
  onUpdate: (id: string, updates: Partial<Activity>) => void
  onDelete: () => void
  onSaveState: () => void
  onManageParticipants?: () => void
}) {
  const [desc, setDesc] = useState(activity.description)
  const [actId, setActId] = useState(activity.id)
  const [performer, setPerformer] = useState(activity.performer)
  const [toolType, setToolType] = useState(activity.tool?.type || '')
  const [toolName, setToolName] = useState(activity.tool?.name || '')
  const [params, setParams] = useState(activity.tool?.parameters || [])
  const [icon, setIcon] = useState(activity.icon || '')
  // Approval end-kind (only meaningful for end nodes). Default an end node to
  // 'approved' so a bound workflow has a valid approved-end out of the box.
  const [endKind, setEndKind] = useState<'approved' | 'rejected'>(activity.endKind || 'approved')
  const [advOpen, setAdvOpen] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  // Re-sync when selection changes. Parameters are reconciled with the tool's
  // definition so blanks fall back to the tool's DefaultValue (an activity
  // selected from the canvas / loaded from XPDL otherwise shows empty boxes).
  useEffect(() => {
    setDesc(activity.description)
    setActId(activity.id)
    setPerformer(activity.performer)
    setToolType(activity.tool?.type || '')
    setToolName(activity.tool?.name || '')
    const def = activity.tool?.name ? getToolDefinition(activity.tool.name) : undefined
    setParams(mergeToolParams(activity.tool?.parameters || [], def))
    setIcon(activity.icon || '')
    setEndKind(activity.endKind || 'approved')
  }, [activity.id, activity.description, activity.performer, activity.tool, activity.icon, activity.endKind, getToolDefinition])

  const handleToolTypeChange = useCallback((newType: string) => {
    setToolType(newType)
    setToolName('')
    setParams([])
    setIcon('')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToolNameChange = useCallback((newName: string) => {
    setToolName(newName)
    const def = getToolDefinition(newName)
    if (def?.FormalParameters?.FormalParameter) {
      setParams(def.FormalParameters.FormalParameter.map(fp => ({
        id: fp.Id,
        value: fp.DefaultValue || '',
        mode: fp.Mode || 'IN',
        dataType: fp.DataType?.BasicType?.Type || 'STRING',
      })))
    } else {
      setParams([])
    }
    if (def?.defaultPerformer) {
      setPerformer(def.defaultPerformer)
    }
    // Auto-populate icon from tool definition
    if (def?.Icon) {
      setIcon(def.Icon)
    }
  }, [getToolDefinition])

  const handleApply = useCallback(() => {
    onUpdate(activity.id, {
      id: actId,
      description: desc,
      performer,
      icon,
      tool: activity.type === 'activity' ? { type: toolType, name: toolName, parameters: params } : activity.tool,
      ...(activity.type === 'end' ? { endKind } : {}),
    })
    onSaveState()
    toast.success('Changes applied')
  }, [activity.id, activity.type, activity.tool, actId, desc, performer, icon, endKind, toolType, toolName, params, onUpdate, onSaveState])

  // Keyboard shortcuts: Enter → Apply, Delete → Delete
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleApply()
    } else if (e.key === 'Delete' && e.ctrlKey) {
      e.preventDefault()
      if (activity.type !== 'start') onDelete()
    }
  }, [handleApply, onDelete, activity.type])

  const distinctTypes = getDistinctToolTypes()
  const toolsByType = toolType ? getToolsByType(toolType) : []

  return (
    <div ref={formRef} onKeyDown={handleKeyDown} style={{ padding: 12, fontSize: '0.8625rem', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: '0.9125rem', fontWeight: 600, color: 'var(--color-text)' }}>
          {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} Properties
        </h4>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6 }}>
          Enter to apply
        </span>
      </div>

      {/* Tool type/name for regular activities */}
      {activity.type === 'activity' && (
        <>
          <FieldGroup label="Activity Type">
            <select value={toolType} onChange={e => handleToolTypeChange(e.target.value)} style={selectStyle}>
              <option value="">Select Activity Type</option>
              {distinctTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FieldGroup>

          <FieldGroup label="Activity/Process/Tool to Invoke">
            <select value={toolName} onChange={e => handleToolNameChange(e.target.value)} disabled={!toolType} style={selectStyle}>
              <option value="">Select Tool</option>
              {toolsByType.map(t => <option key={t.Id} value={t.Id}>{t.Name}</option>)}
            </select>
          </FieldGroup>

          <FieldGroup label="Performer of Activity">
            <select value={performer} onChange={e => setPerformer(e.target.value)} style={selectStyle}>
              <option value="">Select Performer</option>
              {participants.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
              ))}
            </select>
            {onManageParticipants && (
              <button
                type="button"
                onClick={onManageParticipants}
                title="Add or edit the roles (participants) you can choose from"
                style={{
                  marginTop: 4, background: 'none', border: 'none', padding: 0,
                  cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem',
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                }}
              >
                <Users size={11} /> {participants.length === 0 ? 'Add a role…' : 'Manage roles…'}
              </button>
            )}
          </FieldGroup>
        </>
      )}

      <FieldGroup label="Description">
        <input value={desc} onChange={e => setDesc(e.target.value)} style={inputStyle} />
      </FieldGroup>

      {/* End result — for approval end nodes */}
      {activity.type === 'end' && (
        <FieldGroup label="End result">
          <select value={endKind} onChange={e => setEndKind(e.target.value as 'approved' | 'rejected')} style={selectStyle}>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6, marginTop: 2, display: 'block' }}>
            Outcome when the approval reaches this end.
          </span>
        </FieldGroup>
      )}

      {/* Parameter Mapping */}
      {activity.type === 'activity' && params.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <h5 style={{ margin: '0 0 6px', fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-text)' }}>
            Parameter Mapping
          </h5>
          {params.map((p, i) => (
            <FieldGroup key={p.id} label={p.id}>
              <input
                value={p.value}
                onChange={e => {
                  const next = [...params]
                  next[i] = { ...next[i], value: e.target.value }
                  setParams(next)
                }}
                placeholder={p.dataType || 'STRING'}
                style={inputStyle}
              />
            </FieldGroup>
          ))}
        </div>
      )}

      {/* Advanced Settings */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
        <button
          onClick={() => setAdvOpen(!advOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-secondary)', fontSize: '0.8125rem', padding: 0,
          }}
        >
          {advOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Advanced Settings
        </button>
        {advOpen && (
          <div style={{ paddingTop: 6 }}>
            <FieldGroup label="ID">
              <input value={actId} onChange={e => setActId(e.target.value)} style={inputStyle} />
            </FieldGroup>
            {activity.type === 'activity' && (
              <FieldGroup label="Icon">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={icon}
                    onChange={e => setIcon(e.target.value)}
                    placeholder="Set from tool config"
                    title="Auto-set when a tool is selected. Override with any character or emoji."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  {icon && (
                    <span style={{ fontSize: '1.2625rem', lineHeight: 1, flexShrink: 0 }}>{icon}</span>
                  )}
                </div>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6, marginTop: 2, display: 'block' }}>
                  Auto-populated from tool config
                </span>
              </FieldGroup>
            )}
          </div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={handleApply} className="btn btn-primary btn-sm" style={{ flex: 1, fontSize: '0.8125rem' }} title="Apply changes (Enter)">
          <Check size={13} /> Apply
        </button>
        {activity.type !== 'start' && (
          <button onClick={onDelete} className="btn btn-sm" style={{
            flex: 1, fontSize: '0.8125rem',
            background: 'var(--color-danger, #f44336)',
            color: '#fff', border: 'none',
          }} title="Delete (Ctrl+Delete)">
            <Trash2 size={13} /> Delete
          </button>
        )}
      </div>
    </div>
  )
}

// ---- Transition Properties ----

function TransitionProps({
  transition,
  workflow,
  onUpdate,
  onDelete,
  onSaveState,
}: {
  transition: Transition
  workflow: { activities: Activity[] }
  onUpdate: (id: string, updates: Partial<Transition>) => void
  onDelete: () => void
  onSaveState: () => void
}) {
  const [condition, setCondition] = useState(transition.condition)
  const [desc, setDesc] = useState(transition.description)
  const [on, setOn] = useState<'approve' | 'reject'>(transition.on || 'approve')
  // "Always" = the unconditional OTHERWISE edge (no expression).
  const [always, setAlways] = useState(transition.conditionType === 'OTHERWISE')
  const [advOpen, setAdvOpen] = useState(false)

  useEffect(() => {
    setCondition(transition.condition)
    setDesc(transition.description)
    setOn(transition.on || 'approve')
    setAlways(transition.conditionType === 'OTHERWISE')
  }, [transition.id, transition.condition, transition.description, transition.on, transition.conditionType])

  const handleApply = useCallback(() => {
    // OTHERWISE edges carry no expression; a typed condition forces CONDITION.
    const conditionType: 'CONDITION' | 'OTHERWISE' = always ? 'OTHERWISE' : 'CONDITION'
    onUpdate(transition.id, {
      condition: always ? '' : condition,
      description: desc,
      on,
      conditionType,
    })
    onSaveState()
    toast.success('Transition updated')
  }, [transition.id, condition, desc, on, always, onUpdate, onSaveState])

  // Keyboard shortcuts: Enter → Apply, Ctrl+Delete → Delete
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleApply()
    } else if (e.key === 'Delete' && e.ctrlKey) {
      e.preventDefault()
      onDelete()
    }
  }, [handleApply, onDelete])

  const fromAct = workflow.activities.find(a => a.id === transition.from)
  const toAct = workflow.activities.find(a => a.id === transition.to)

  return (
    <div onKeyDown={handleKeyDown} style={{ padding: 12, fontSize: '0.8625rem', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: '0.9125rem', fontWeight: 600, color: 'var(--color-text)' }}>
          Transition Properties
        </h4>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6 }}>
          Enter to apply
        </span>
      </div>

      <FieldGroup label="On decision">
        <select value={on} onChange={e => setOn(e.target.value as 'approve' | 'reject')} style={selectStyle}>
          <option value="approve">Approve</option>
          <option value="reject">Reject</option>
        </select>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6, marginTop: 2, display: 'block' }}>
          Which human decision takes this edge.
        </span>
      </FieldGroup>

      <FieldGroup label="Routing">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8425rem', color: 'var(--color-text)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={always}
            onChange={e => setAlways(e.target.checked)}
          />
          Always (otherwise) — no condition
        </label>
      </FieldGroup>

      <FieldGroup label="Condition Expression">
        <input
          value={always ? '' : condition}
          onChange={e => setCondition(e.target.value)}
          disabled={always}
          placeholder={always ? 'Unconditional edge' : ''}
          style={{ ...inputStyle, opacity: always ? 0.5 : 1 }}
        />
      </FieldGroup>

      <FieldGroup label="Description">
        <input value={desc} onChange={e => setDesc(e.target.value)} style={inputStyle} />
      </FieldGroup>

      {/* Advanced */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
        <button
          onClick={() => setAdvOpen(!advOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-secondary)', fontSize: '0.8125rem', padding: 0,
          }}
        >
          {advOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Advanced Settings
        </button>
        {advOpen && (
          <div style={{ paddingTop: 6 }}>
            <FieldGroup label="ID">
              <input value={transition.id} readOnly style={{ ...inputStyle, opacity: 0.6 }} />
            </FieldGroup>
            <FieldGroup label="From Process">
              <input value={fromAct ? `${fromAct.description} (${fromAct.id})` : transition.from} readOnly style={{ ...inputStyle, opacity: 0.6 }} />
            </FieldGroup>
            <FieldGroup label="To Process">
              <input value={toAct ? `${toAct.description} (${toAct.id})` : transition.to} readOnly style={{ ...inputStyle, opacity: 0.6 }} />
            </FieldGroup>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={handleApply} className="btn btn-primary btn-sm" style={{ flex: 1, fontSize: '0.8125rem' }} title="Apply changes (Enter)">
          <Check size={13} /> Apply
        </button>
        <button onClick={onDelete} className="btn btn-sm" style={{
          flex: 1, fontSize: '0.8125rem',
          background: 'var(--color-danger, #f44336)',
          color: '#fff', border: 'none',
        }} title="Delete (Ctrl+Delete)">
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  )
}

// ---- Shared styled helpers ----

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{
        display: 'block',
        fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        marginBottom: 2,
        fontWeight: 500,
      }}>
        {label}
      </label>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 8px',
  fontSize: '0.8625rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
}
